import { applySnapshot, flow, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { v4 } from 'uuid';
import { DeleteDocumentAsync, DownloadDocumentAsync, UploadDocumentAsync,
         GetDocInfoListByRecipientId, UploadDocInfoAsync, DeleteDocInfoAsync } from '../Services/DocumentService';
import axios from 'axios';
import DocumentInfo from '../Models/DocumentInfo';


export const DocumentInfoStore = types
  .model('DocumentInfoStore', {
    tableHeaders: types.array(types.string),
    infoItems: types.array(DocumentInfo),
    isLoading: types.boolean,
    uploadMessage: types.string,
    tableMessage: types.string,
    uploadProgress: types.optional(types.number, 0),
  })
  .actions((self) => {
    const wrapLoadingDuringAction = flow(function* (serviceAction: any) {
      self.isLoading = true;
      try {
        yield serviceAction();
      } catch (err) {
        console.error(err);
      }
      self.isLoading = false;
    });

    return {
      setUploadMessage(message: string) {
        self.uploadMessage = message;
      },
      setTableMessage(message: string) {
        self.tableMessage = message;
      },
      setUploadProgress(message: number) {
        self.uploadProgress = message;
      },
      loadInfoTableHeaders: flow(function* () {
        yield wrapLoadingDuringAction(
          flow(function* () {
            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const result = yield axios.get(`${baseUrl}/documents/table-details`);
            applySnapshot(self.tableHeaders, result.data ?? {});
          })
        );
      }),
      loadTableDataByRecipientId: flow(function* (recipientId: string) {
        yield wrapLoadingDuringAction(
          flow(function* () {
            const result = yield GetDocInfoListByRecipientId(recipientId);

            self.infoItems.clear();
            applySnapshot(self.infoItems, result.data ?? {});
          })
        );
      }),
      uploadDocument: flow(function* (
        documentEncodedString: string,
        caregiverId: string,
        recipientId: string, 
        recipientName: string,
        domain: string,
        tasks: string, 
        file: File,
        onProgress: (progressEvent: ProgressEvent) => void) {
          
          yield wrapLoadingDuringAction(
            flow(function* () {
              self.isLoading = true;
              const docId = v4();     
              
              const docResult = yield UploadDocumentAsync(documentEncodedString, docId, file.name, file.type, caregiverId, recipientId,onProgress);
              if(docResult !== 200) {
                self.uploadMessage = "Error: There was an error uploading the document. Please try again.";
                return;
              }

              const infoResult = yield UploadDocInfoAsync(docId, caregiverId, recipientId, recipientName, domain, tasks, file);
              if(infoResult !== 200) {
                self.uploadMessage = "Error: There was an error uploading the document information. Please try again.";
                return;
              }

              const result = yield GetDocInfoListByRecipientId(recipientId);

              self.infoItems.clear();
              applySnapshot(self.infoItems, result.data ?? {});
              self.isLoading = false;
            })
          );
      }),
      deleteDocument: flow(function* (
        documentId: string,
        documentName: string,
        caregiverId: string,
        recipientId: string ) {
          yield wrapLoadingDuringAction(
            flow(function* () {
              self.isLoading = true;

              const docResult = yield DeleteDocumentAsync(documentId, documentName, caregiverId, recipientId);
              if(docResult !== 200) {
                self.tableMessage = `Error: There was an error deleting the document: ${documentName}. Please try again.`;
                return;
              }

              const infoResult = yield DeleteDocInfoAsync(documentId); 
              if(infoResult !== 200) {
                self.tableMessage = `Error: There was an error deleting the document information for: ${documentName}. Please try again.`;
                return;
              }

              self.infoItems.splice(self.infoItems.findIndex(x => x.documentId === documentId), 1);
              self.isLoading = false;
            })
          );
      }),
      downloadDocument: flow(function* (
        documentId: string,
        documentName: string,
        caregiverId: string,
        recipientId: string ) {
          yield wrapLoadingDuringAction(
            flow(function* () {
              self.isLoading = true;
              yield DownloadDocumentAsync(documentId, documentName, caregiverId, recipientId);
              self.isLoading = false;
            })
          );
      }),

    };
  });

export interface IDocumentInfoStore extends Instance<typeof DocumentInfoStore> {}
export interface IDocumentInfoSnapshotIn extends SnapshotIn<typeof DocumentInfo> {}
